<script setup lang="ts">
import HeartEmpty from '@/assets/icons/2States/HeartEmpty.vue';
import HeartFull from '@/assets/icons/2States/HeartFull.vue';
import LocationIcon from '@/assets/icons/Suppliers/LocationIcon.vue';
import { useFetch } from '@/composables/useFetch.js';
import type { MessageSchema } from '@/i18n/index.js';
import { useEnvStore } from '@/stores/envStore.js';
import { useUserStore } from '@/stores/useUserStore';
import type { Supplier } from '@/types/api.js';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

defineProps<{
  supplier: Supplier;
  isFavorite: boolean;
}>();

const emit = defineEmits<{
  (e: 'updateFavorite', add: boolean): void;
}>();

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const user = ref(useUserStore().user);
const { fetchData } = useFetch();

async function toggleFavorite(supplierUuid: string) {
  if (!user.value) return;
  const restaurantId = user.value.restaurants[0].id;
  fetchData(`${useEnvStore().apiUrl}/restaurants/${restaurantId}/suppliers/${supplierUuid}/toggle-favorite`, 'PATCH').then((response) => {
    if (response.data) {
      emit('updateFavorite', response.data.status === 'added');
    } else {
      console.log(response.error);
    }
  });
}
</script>

<template>
  <div class="w-full space-y-3 rounded-xl border-2 border-primary-300 p-3 shadow-md">
    <div class="flex flex-col gap-1">
      <div class="items-top flex justify-between">
        <div class="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
          <span class="font-bold text-xl text-primary-800">{{ supplier.name }}</span>
        </div>
        <Button
          v-if="!supplier.is_custom"
          link
          rounded
          :pt="{ root: { class: '!p-1.5 !w-8 !h-8' } }"
          :pt-options="{ mergeProps: true }"
          @click.stop="toggleFavorite(supplier.uuid)"
        >
          <template #icon>
            <HeartFull v-if="isFavorite" />
            <HeartEmpty v-else />
          </template>
        </Button>
        <div v-else class="flex h-fit w-fit items-center justify-center rounded-md border border-primary-500 px-1 text-sm text-primary-500">
          {{ t('profile.suppliers.custom') }}
        </div>
      </div>

      <div v-if="supplier.tags.length > 0" class="flex gap-2 text-sm text-primary-800">
        <Tag v-for="tag in supplier.tags" :key="tag.id">
          {{ t('profile.suppliers.tags.' + tag.name) }}
        </Tag>
      </div>
    </div>

    <div v-if="supplier.address || supplier.logo_url" class="max-h-18 flex w-full justify-between gap-2 sm:max-h-24">
      <div class="flex flex-col justify-between gap-2">
        <div v-if="supplier.address" class="flex gap-2 text-sm text-primary-800">
          <span class="block aspect-square w-4 shrink-0"><LocationIcon /></span>
          <span class="line-clamp-1"> {{ supplier.address.city.postal_code }} {{ supplier.address.city.name }} </span>
        </div>
      </div>
      <div v-if="supplier.logo_url" class="h-20 w-1/3 shrink-0 sm:h-24">
        <img :src="supplier.logo_url" class="h-full w-full object-contain" alt="{{ supplier.name }} logo" />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
